import { trackAddNewUserInChat } from './tracking';
import { IMessageEvent, IWsFileMessage, IWsMessageEvent } from './types';
import { BLOB_ID } from '../../constants/blobId';
import { IOfferSchema } from '../../repositories/chats/entities/get_my_offers_for_selection/OfferSchema';
import { currentChatOpponentIdSelector, currentOpenChatSelector } from '../../selectors';
import { setSocketStatusFailed } from '../../slices/socketStatusSlice';
import { ERequestApiStatus, EWsClientEventType } from '../../types/appState';
import { TMessageSchema } from '../../types/messages';
import { TThunkAction } from '../../types/redux/thunk';
import { TSessionSucceedState } from '../../types/session';
import { ETelemetryEvents } from '../../types/telemetry';
import { timeoutPromise } from '../../utils/timeoutPromise';
import { EInternetStatus } from '../internetStatus';
import { setSendMessageFailed } from '../sendMessage';

/**
 * Отправка сообщения по WS
 */

interface IProps {
  message: TMessageSchema;
  files?: IWsFileMessage[];
  offers?: IOfferSchema[];
}

const DOMAIN = 'critical.messageSend';

export const sendWsMessage =
  ({ message, files, offers }: IProps): TThunkAction<Promise<void>> =>
  async (dispatch, getState, { logger, telemetry, custom: { messagesMap, socket } }) => {
    if (!socket) {
      return;
    }

    const { clientEvents, eventChannel } = socket;

    const state = getState();

    const {
      session: { status, token },
      socketStatus,
      internetStatus,
    } = state;

    const opponentId = currentChatOpponentIdSelector(state);
    const { users, offer } = currentOpenChatSelector(state) || {};

    // в модалке каждый раз когда возвращемся на экран - перезапрашивается сессия
    const isSessionExpired = status !== ERequestApiStatus.Succeed && status !== ERequestApiStatus.Pending;
    const isSocketIsClose = eventChannel.isChannelClosed() || socketStatus === ERequestApiStatus.Failed;
    const isOffline = internetStatus === EInternetStatus.Offline;

    const { chatId, messageId, text, userId, payload } = message;

    const isMessageUserInChat = users?.find(user => user.userId === userId);

    const messageEvent: IMessageEvent = {
      chatId,
      status: ERequestApiStatus.Pending,
      type: EWsClientEventType.Message,
      timestamp: Date.now(),
    };

    if (isSocketIsClose) {
      dispatch(setSocketStatusFailed());
    }

    if (isSessionExpired || isSocketIsClose || isOffline || !chatId || chatId === BLOB_ID) {
      clientEvents.set(messageId, { ...messageEvent, status: ERequestApiStatus.Failed });

      dispatch(setSendMessageFailed({ message, messageId, messagesMap }));

      telemetry.increment(ETelemetryEvents.ChatWsMessageSendFail);
      logger.warning('Не удалось отправить сообщение по ws', {
        domain: 'src/shared/actions/wsMessage/sendWsMessage.ts',
        message: 'Сессия истекла или соединение закрыто',
      });

      return;
    }

    clientEvents.set(messageId, messageEvent);

    const wsMessageEvent: IWsMessageEvent = {
      type: EWsClientEventType.Message,
      message: {
        chatId,
        text,
        files,
        offers,
        payload,
      },
      uid: messageId,
      userId,
      sessionToken: (token as TSessionSucceedState['token']).sessionToken,
    };

    telemetry.increment(ETelemetryEvents.ChatWsMessageSendInit);

    try {
      await timeoutPromise(eventChannel.sendMessage(wsMessageEvent), 10000);

      if (!isMessageUserInChat) {
        trackAddNewUserInChat({ offer, chatId, opponentId });
      }
    } catch (err) {
      telemetry.increment(ETelemetryEvents.ChatWsMessageSendFail);

      logger.error({ ...err, domain: DOMAIN }, { degradation: 'Отправка сообщения' });

      clientEvents.set(messageId, { ...messageEvent, status: ERequestApiStatus.Failed });

      dispatch(setSendMessageFailed({ message, messageId, messagesMap }));
    }
  };
